import React, { useState, useRef, Component } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type PageView = 'home' | 'archive' | 'detail' | 'configurator' | 'warranty' | 'instructions' | 'contact' | 'media' | 'dealers';
interface VehicleModel {
  name: string;
  years?: string;
  categories: string[];
}
interface MakeMenu {
  make: string;
  models: VehicleModel[];
}
const megaMenuData: MakeMenu[] = [{
  make: 'Chevy',
  models: [{
    name: '1500 Silverado',
    years: '2019–2024',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: '2500 Silverado',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: '3500 Silverado',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: 'Tahoe / Suburban',
    categories: ['Leveling Kit', 'Lift Kit', 'Replacement Parts', 'Accessories']
  }]
}, {
  make: 'GMC',
  models: [{
    name: 'Sierra 1500',
    years: '2019–2024',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: 'Sierra 2500',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: 'Sierra 3500',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: 'Yukon / Yukon XL',
    categories: ['Leveling Kit', 'Lift Kit', 'Replacement Parts', 'Accessories']
  }]
}, {
  make: 'Ford',
  models: [{
    name: 'F-150',
    years: '2020–2026 WT, Custom, LT, LTZ, High Country',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: 'Bronco',
    years: '2021–2025',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: 'Ranger',
    years: '2019–2023',
    categories: ['Leveling Kit', 'Lift Kit', 'Replacement Parts', 'Accessories']
  }, {
    name: 'Super Duty F-250/350',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }]
}, {
  make: 'Ram',
  models: [{
    name: '1500',
    years: '2011–2019',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: '1500 Classic',
    years: '2019–2023',
    categories: ['Leveling Kit', 'Lift Kit', 'Replacement Parts', 'Accessories']
  }, {
    name: '2500 / 3500',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }, {
    name: 'TRX',
    categories: ['Leveling Kit', 'Lift Kit', 'Steering Components', 'Replacement Parts', 'Accessories']
  }]
}];
const utilityLinks: {
  label: string;
  action: PageView;
}[] = [{
  label: 'Warranty',
  action: 'warranty'
}, {
  label: 'Instructions',
  action: 'instructions'
}, {
  label: 'Contact',
  action: 'contact'
}];
interface NavigationProps {
  onNavigate?: (page: PageView, productId?: number) => void;
}
export function Navigation({
  onNavigate
}: NavigationProps) {
  const [activeMenu, setActiveMenu] = useState<string | null>('Ford');
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMouseEnter = (_make: string) => {};
  const handleMouseLeave = () => {};
  const handleDropdownMouseEnter = () => {};
  const handleNavigate = (page: PageView) => {
    setActiveMenu(null);
    onNavigate && onNavigate(page);
  };
  const activeMakeData = megaMenuData.find(m => m.make === activeMenu);
  return <div className="relative z-50" data-id="element-135">
      <nav className="w-full bg-primary text-white shadow-md" data-id="element-136">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-[52px] flex items-center justify-between" data-id="element-137">
          {/* Left: Main Nav */}
          <ul className="flex items-center h-full" data-id="element-138">
            {/* Home */}
            <li className="h-full" data-id="element-139">
              <button onClick={() => handleNavigate('home')} className="h-full flex items-center px-5 font-heading font-bold text-lg uppercase italic tracking-wide hover:bg-black/10 transition-colors border-r border-white/10" data-id="element-140">
                Home
              </button>
            </li>

            {/* Vehicle Makes with Mega-Menu */}
            {megaMenuData.map(({
            make
          }) => <li key={make} className="h-full relative" onMouseEnter={() => handleMouseEnter(make)} onMouseLeave={handleMouseLeave} data-id="element-141">
                <button className={`h-full flex items-center gap-1 px-5 font-heading font-bold text-lg uppercase italic tracking-wide transition-colors border-r border-white/10
                    ${activeMenu === make ? 'bg-black/20' : 'hover:bg-black/10'}`} data-id="element-142">
                  {make}
                  <ChevronDown className={`w-4 h-4 mt-0.5 opacity-80 transition-transform duration-200 ${activeMenu === make ? 'rotate-180' : ''}`} data-id="element-143" />
                </button>
              </li>)}

            {/* Build Your Kit */}
            <li className="h-full" data-id="element-144">
              <button onClick={() => handleNavigate('configurator')} className="h-full flex items-center px-5 font-heading font-bold text-lg uppercase italic tracking-wide bg-black/20 hover:bg-black/30 transition-colors border-r border-white/10" data-id="element-145">
                Build Your Kit
              </button>
            </li>
          </ul>

          {/* Right: Utility Links */}
          <ul className="flex items-center gap-6" data-id="element-146">
            {utilityLinks.map(link => <li key={link.label} data-id="element-147">
                <button onClick={() => handleNavigate(link.action)} className="font-heading text-base font-medium italic hover:text-charcoal transition-colors tracking-wide opacity-90 hover:opacity-100" data-id="element-148">
                  {link.label}
                </button>
              </li>)}
          </ul>
        </div>
      </nav>

      {/* Mega-Menu Dropdown */}
      <AnimatePresence data-id="element-149">
        {activeMenu && activeMakeData && <motion.div key={activeMenu} initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="absolute left-0 w-full bg-charcoal border-t-[3px] border-primary shadow-2xl" onMouseEnter={handleDropdownMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-150">
            <div className="max-w-[1400px] mx-auto px-8 py-8" data-id="element-151">
              {/* Model Columns */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-6" data-id="element-152">
                {activeMakeData.models.map(model => <div key={model.name} data-id="element-153">
                    <h4 className="font-heading font-bold text-xl uppercase italic text-white mb-1 leading-tight" data-id="element-154">
                      {model.name}
                    </h4>
                    {model.years && <p className="text-xs text-gray-400 mb-3 leading-snug font-sans" data-id="element-155">
                        {model.years}
                      </p>}
                    {!model.years && <div className="mb-3" data-id="element-156" />}
                    <ul className="space-y-1.5" data-id="element-157">
                      {model.categories.map(cat => <li key={cat} data-id="element-158">
                          <button onClick={() => handleNavigate('archive')} className="text-sm text-gray-300 hover:text-primary transition-colors font-sans text-left w-full" data-id="element-159">
                            {cat}
                          </button>
                        </li>)}
                    </ul>
                  </div>)}
              </div>

              {/* Footer row */}
              <div className="border-t border-gray-700 pt-4 flex items-center justify-between" data-id="element-160">
                <p className="text-xs text-gray-500 font-sans" data-id="element-161">
                  Select a category above to browse {activeMakeData.make}{' '}
                  products
                </p>
                <button onClick={() => handleNavigate('archive')} className="flex items-center gap-2 text-primary font-heading font-bold text-sm uppercase italic hover:gap-3 transition-all" data-id="element-162">
                  View All {activeMakeData.make} Products{' '}
                  <ArrowRight className="w-4 h-4" data-id="element-163" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}